import type { Note } from '@/redux/internal-services/notesApi';

/** Colored status badge classes — gray draft, amber processing, blue ready, green saved, red failed. */
export const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-ui-subtle text-ui-text3',
  recording: 'bg-amber-500/15 text-amber-600',
  transcribing: 'bg-amber-500/15 text-amber-600',
  analyzing: 'bg-amber-500/15 text-amber-600',
  ready: 'bg-brand-bright/15 text-brand-dark',
  saved: 'bg-success/15 text-success',
  discarded: 'bg-ui-subtle text-ui-text3',
  failed: 'bg-red-500/15 text-red-600',
};

/** First sentence / snippet for a note card preview. */
export function noteSnippet(n: Note): string {
  const body = (n.summary || n.clean_transcript || n.final_transcript || '').trim();
  if (!body) return '';
  const firstSentence = body.split(/(?<=[.!?])\s/)[0];
  return firstSentence.length > 140 ? `${firstSentence.slice(0, 140)}…` : firstSentence;
}

/** Card heading: explicit title, else a short lead from the content. */
export function noteTitle(n: Note): string {
  if (n.title && n.title.trim()) return n.title.trim();
  const snippet = noteSnippet(n);
  if (!snippet) return 'Untitled note';
  return snippet.length > 60 ? `${snippet.slice(0, 60)}…` : snippet;
}

/**
 * Parse a backend timestamp into a Date. Timestamps that lack a timezone
 * designator (no trailing `Z` or `±HH:MM` offset) are naive UTC values —
 * without this the browser parses them as *local* time, so a note created
 * seconds ago shows as hours ago in non-UTC timezones (e.g. +5.5h in IST).
 */
function parseTimestamp(iso: string): Date {
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasTz ? iso : `${iso}Z`);
}

/** Compact relative time, e.g. "just now", "2 min ago", "3 h ago", "5 d ago", else a date. */
export function relativeTime(iso: string): string {
  const then = parseTimestamp(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffS = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffS < 45) return 'just now';
  const mins = Math.floor(diffS / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} d ago`;
  // toLocaleDateString renders in the user's local timezone.
  return parseTimestamp(iso).toLocaleDateString();
}
